import type {CaseInput, CustomMaterial, MaterialCapability, MaterialSymbol} from "./types";

export const FALLBACK_MATERIALS: MaterialCapability[] = [
  ["Be", "Beryllium", 4, 9.012, 3.32, 1.66, 2.32, 2.5, "parameter_supported", "Yamamura-Tawara parameter table", false],
  ["B", "Boron", 5, 10.8, 5.77, 2.62, 4.39, 2.5, "parameter_supported", "Yamamura-Tawara parameter table", false],
  ["C", "Carbon", 6, 12.01, 7.37, 1.7, 1.84, 2.5, "paper_and_legacy", "Yamamura-Tawara; Liu 2023", true],
  ["Al", "Aluminium", 13, 26.98, 3.39, 1.0, 2.17, 2.5, "parameter_supported", "Yamamura-Tawara parameter table", false],
  ["Si", "Silicon", 14, 28.085, 4.63, 0.66, 2.32, 2.5, "parameter_supported", "Yamamura-Tawara parameter table", false],
  ["Fe", "Iron", 26, 55.845, 4.28, 0.75, 1.2, 2.5, "parameter_supported", "Yamamura-Tawara parameter table", false],
  ["W", "Tungsten", 74, 183.84, 8.9, 0.72, 2.14, 2.8, "paper_and_legacy", "Yamamura-Tawara; Escande 2022", false],
  ["Au", "Gold", 79, 196.967, 3.81, 1.08, 1.64, 2.8, "parameter_supported", "Yamamura-Tawara parameter table", false],
].map(([symbol, name, z, mass, binding, q, w, s, qualification, source, chemical]) => ({
  symbol: symbol as MaterialCapability["symbol"], name: name as string, atomic_number: z as number, atomic_mass_u: mass as number,
  surface_binding_energy_ev: binding as number, q: q as number, w: w as number, s: s as number,
  qualification: qualification as MaterialCapability["qualification"], source: source as string,
  chemical_sputtering_supported: chemical as boolean, chemical_sputtering_default: chemical as boolean,
}));

export const DEFAULT_CUSTOM: CustomMaterial = {
  name: "Custom target", atomic_number: 22, atomic_mass_u: 47.867,
  surface_binding_energy_ev: 4.89, q: 0.8, w: 1.5, s: 2.5,
};

export function makeCase(index: number, target: MaterialSymbol = index === 0 ? "C" : "W"): CaseInput {
  return {
    case_id: `Case ${caseLetter(index)}`,
    target,
    include_chemical_sputtering: target === "C",
    minor_radius_m: 0.5,
    lambda_m: 0.05,
    d_perp_m2_s: 1,
    fraction_f: 0.1,
    radiation_coefficient_w_m3: 1e-31,
    sheath_gamma: 7,
    energy_min_ev: 0,
    energy_max_ev: 100000,
    tt_min_ev: 0.01,
    tt_max_ev: 100,
    scan_points: 1000,
    integration_points: 1000,
    ...(target === "custom" ? {custom_material: {...DEFAULT_CUSTOM}} : {}),
  };
}

export function caseLetter(index: number): string {
  let label = "";
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) label = String.fromCharCode(65 + (n - 1) % 26) + label;
  return label;
}

export function cloneCase(cases: CaseInput[], source: number): CaseInput {
  let index = 0;
  while (cases.some(item => item.case_id === `Case ${caseLetter(index)}`)) index++;
  return {...structuredClone(cases[source]), case_id: `Case ${caseLetter(index)}`};
}

// Keep each synchronous request within the server budget; the workspace has no case-count cap.
export function batchCases(cases: CaseInput[], budget: number): CaseInput[][] {
  const batches: CaseInput[][] = [];
  let batch: CaseInput[] = [], work = 0, bytes = 0;
  for (const input of cases) {
    const cost = input.scan_points * input.integration_points;
    if (cost > budget) throw new Error("Single case exceeds compute budget");
    const size = new TextEncoder().encode(JSON.stringify(input)).length + 1;
    if (batch.length && (work + cost > budget || bytes + size > 128 * 1024)) {
      batches.push(batch); batch = []; work = 0; bytes = 0;
    }
    batch.push(input); work += cost; bytes += size;
  }
  if (batch.length) batches.push(batch);
  return batches;
}

export function changeTarget(input: CaseInput, target: MaterialSymbol): CaseInput {
  return {
    ...input,
    target,
    include_chemical_sputtering: target === "C" && (input.projectile ?? "D") === "D",
    ...(target === "custom" ? {custom_material: input.custom_material ?? {...DEFAULT_CUSTOM}} : {custom_material: undefined}),
  };
}

export function validateCase(input: CaseInput): string[] {
  const errors: string[] = [];
  if (!/^[A-Za-z0-9_. -]{1,64}$/.test(input.case_id)) errors.push("case_id");
  if (!(input.minor_radius_m > 0 && input.lambda_m > 0 && input.lambda_m < input.minor_radius_m)) errors.push("lambda_m");
  if (!(input.d_perp_m2_s > 0)) errors.push("d_perp_m2_s");
  if (!(input.fraction_f > 0 && input.fraction_f <= 1)) errors.push("fraction_f");
  if (!(input.radiation_coefficient_w_m3 > 0)) errors.push("radiation_coefficient_w_m3");
  if (!(input.energy_min_ev >= 0 && input.energy_max_ev > input.energy_min_ev)) errors.push("energy_max_ev");
  if (!(input.tt_min_ev > 0 && input.tt_max_ev >= input.tt_min_ev)) errors.push("tt_max_ev");
  if (!(Number.isInteger(input.scan_points) && input.scan_points >= 1 && input.scan_points <= 2000)) errors.push("scan_points");
  if (!(Number.isInteger(input.integration_points) && input.integration_points >= 10 && input.integration_points <= 100000)) errors.push("integration_points");
  if ((input.target !== "C" || (input.projectile ?? "D") !== "D") && input.include_chemical_sputtering) errors.push("include_chemical_sputtering");
  if (input.target === "custom" && !input.custom_material) errors.push("custom_material");
  return errors;
}
